import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { TabPanelList, csx } from '@vtex/admin-ui'

import type { DrawerProps } from '../../../../../../ui/components'
import { Tab, TabPanel, Tabs, useTabState } from '../../../../components'
import { DrawerCloseButton, Drawer } from '../../../../../../ui/components'
import type { Package } from '../../../../../../core/typings'
import { PickupsPointsContent } from './PickupsPointsContent'
import { DeliveryMethodsContent } from './DeliveryMethodsContent'
import { DeliveryChannel } from '../../../../constants'
import { TakeAwayContent } from './TakeAwayContent'
import { ChangeShippingMethodDrawerTitle } from './ChangeShippingMethodDrawerTitle'
import { useChangeShippingMethodDrawer } from '../ShippingPackage/hooks'
import { useCustomConfig } from '../../../../../../core/hooks/useCustomConfig'
import type { OrderFormItemWithAssemblyItems } from '../../../Cart/typings'

export type ChangeShippingMethodDrawerProps = DrawerProps & {
  currentPackage: Package
  item: OrderFormItemWithAssemblyItems
  onItemSlaChange: () => void
}

export const ChangeShippingMethodDrawer = ({
  isOpen,
  onClose,
  currentPackage,
  item,
  onItemSlaChange,
  ...props
}: ChangeShippingMethodDrawerProps) => {
  const {
    changePickupPointOfItem,
    changeDeliveryMethodOfItem,
    deliveryMethods,
    pickupPointsByCurrentLocation,
    changeItemToTakeAway,
    accountHasTakeAwayAvailable,
    packageHasTakeAwayAvailable,
  } = useChangeShippingMethodDrawer({
    currentPackage,
    item: item.orderFormItem.item,
  })

  const { sellWithoutStockInHands } = useCustomConfig()

  const tabState = useTabState({
    defaultSelectedId: currentPackage.selectedSlaType,
  })

  const shouldShowTakeAwayTab =
    accountHasTakeAwayAvailable || sellWithoutStockInHands

  const shouldShowDeliveryTab = deliveryMethods.length > 0
  const shouldShowPickupsTab = pickupPointsByCurrentLocation.length > 0

  const shouldShowTabs =
    [shouldShowTakeAwayTab, shouldShowDeliveryTab, shouldShowPickupsTab].filter(
      (isTabAvailable) => isTabAvailable
    ).length > 1

  const handleDeliveryMethodDrawerSubmit = (selectedDeliveryMethodSla: SLA) => {
    changeDeliveryMethodOfItem(
      item.orderFormItem.item.id,
      selectedDeliveryMethodSla
    )

    onItemSlaChange()

    onClose()
  }

  const handlePickupSubmit = (selectedPickupId: string) => {
    changePickupPointOfItem(item.orderFormItem.item.id, selectedPickupId)

    onItemSlaChange()

    onClose()
  }

  const handleTakeAwaySubmit = () => {
    changeItemToTakeAway(item.orderFormItem.item.id)

    onItemSlaChange()

    onClose()
  }

  useEffect(() => {
    if (!isOpen) {
      tabState.setSelectedId(currentPackage.selectedSlaType)
    }
  }, [isOpen, tabState, currentPackage.selectedSlaType])

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      {...props}
      data-testid="change-shipping-method-drawer"
      className={csx({ paddingBottom: '$space-5' })}
    >
      <DrawerCloseButton className={csx({ marginBottom: '$space-5' })} />

      <ChangeShippingMethodDrawerTitle>
        {item.orderFormItem.item.name}
      </ChangeShippingMethodDrawerTitle>

      <Tabs
        state={tabState}
        aria-label="Shipping methods"
        className={csx({ marginTop: '$space-4' })}
      >
        <div
          className={csx({
            display: shouldShowTabs ? 'flex' : 'none',
            paddingX: '$space-0',
            columnGap: '0.5rem',
            justifyContent: 'space-between',
            flex: 1,
          })}
        >
          {shouldShowTakeAwayTab && (
            <Tab id={DeliveryChannel.TakeAway}>
              <FormattedMessage id="checkout.shipping.select-shipping-method.to-takeaway" />
            </Tab>
          )}
          {shouldShowDeliveryTab && (
            <Tab id={DeliveryChannel.Delivery}>
              <FormattedMessage id="checkout.shipping.select-shipping-method.to-delivery" />
            </Tab>
          )}
          {shouldShowPickupsTab && (
            <Tab id={DeliveryChannel.PickupInPoint}>
              <FormattedMessage id="checkout.shipping.select-shipping-method.to-pickup" />
            </Tab>
          )}
        </div>
      </Tabs>
      <TabPanelList state={tabState}>
        {shouldShowTakeAwayTab && (
          <TabPanel id={DeliveryChannel.TakeAway} state={tabState}>
            <TakeAwayContent
              isOpen={isOpen}
              currentPackage={currentPackage}
              item={item.orderFormItem.item}
              onDeliveryMethodSubmit={handleDeliveryMethodDrawerSubmit}
              onSubmit={handleTakeAwaySubmit}
              packageHasTakeAwayAvailable={packageHasTakeAwayAvailable}
            />
          </TabPanel>
        )}
        {shouldShowDeliveryTab && (
          <TabPanel state={tabState} id={DeliveryChannel.Delivery}>
            <DeliveryMethodsContent
              isOpen={isOpen}
              deliveryMethodsList={deliveryMethods}
              currentPackage={currentPackage}
              onDeliveryMethodSubmit={handleDeliveryMethodDrawerSubmit}
            />
          </TabPanel>
        )}
        {shouldShowPickupsTab && (
          <TabPanel state={tabState} id={DeliveryChannel.PickupInPoint}>
            <PickupsPointsContent
              isOpen={isOpen}
              pickupPointsList={pickupPointsByCurrentLocation}
              currentPackage={currentPackage}
              onPickupSubmit={handlePickupSubmit}
            />
          </TabPanel>
        )}
      </TabPanelList>
    </Drawer>
  )
}
